import React from 'react';
import { motion } from 'framer-motion';
import { Award, BookOpen, Heart, Users } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const achievements = [{
  icon: Users,
  number: '10,000+',
  label: 'Students Guided'
}, {
  icon: BookOpen,
  number: '12',
  label: 'Books Published'
}, {
  icon: Award,
  number: '40+',
  label: 'Years Teaching'
}, {
  icon: Heart,
  number: '500+',
  label: 'Retreats Led'
}];
const timeline = [{
  period: '1950-1970',
  title: 'Early Life & Spiritual Awakening',
  description: 'Born in a small Himalayan village, Satguru Dev showed early signs of spiritual inclination. At age 15, he embarked on a pilgrimage that would change his life, meeting his first guru in the caves of Rishikesh.'
}, {
  period: '1970-1985',
  title: 'Years of Intense Practice',
  description: 'Spent 15 years in deep meditation and study under various masters across India and Tibet. Mastered ancient techniques of pranayama, kundalini yoga, and Vedantic philosophy.'
}, {
  period: '1985',
  title: 'Founding of Supradhyan',
  description: 'Received the calling to share his wisdom with the world. Established Supradhyan Enlightenment Center with a vision to make authentic spiritual teachings accessible to modern seekers.'
}, {
  period: '1990-2000',
  title: 'Global Expansion',
  description: 'Began teaching internationally, establishing satellite centers across Asia, Europe, and North America. Published his first books on meditation and conscious living.'
}, {
  period: '2000-Present',
  title: 'Legacy of Transformation',
  description: 'Continues to guide thousands through retreats, online courses, and personal consultations. Dedicated to preserving ancient wisdom while adapting practices for contemporary life.'
}];
export function SatguruBioPage() {
  return <div className="min-h-screen bg-[#FFFBF5]" data-id="element-814">
      <Header data-id="element-815" />

      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px] w-full overflow-hidden" data-id="element-816">
        <motion.div initial={{
        scale: 1.1,
        opacity: 0
      }} animate={{
        scale: 1,
        opacity: 1
      }} transition={{
        duration: 1.2,
        ease: 'easeOut'
      }} className="absolute inset-0" data-id="element-817">
          <img src="https://images.unsplash.com/photo-1518199266791-5375a83190b7?q=80&w=2070&auto=format&fit=crop" alt="Satguru Dev in meditation" className="w-full h-full object-cover" data-id="element-818" />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-[#FFFBF5]" data-id="element-819" />
        </motion.div>

        <div className="absolute bottom-0 left-0 right-0 pb-16" data-id="element-820">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-821">
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: 0.3
          }} className="max-w-4xl" data-id="element-822">
              <h1 className="font-serif text-5xl md:text-7xl text-white mb-6 leading-tight drop-shadow-2xl" data-id="element-823">
                Satguru Dev
              </h1>
              <p className="text-xl md:text-2xl text-white/95 leading-relaxed drop-shadow-lg" data-id="element-824">
                Spiritual Guide, Teacher, and Founder
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Achievements */}
      <section className="py-20 bg-white" data-id="element-825">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-826">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8" data-id="element-827">
            {achievements.map((achievement, index) => <motion.div key={index} initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.1
          }} className="text-center" data-id="element-828">
                <div className="w-16 h-16 rounded-full bg-[#8B9D83]/20 flex items-center justify-center mx-auto mb-4" data-id="element-829">
                  <achievement.icon className="w-8 h-8 text-[#8B9D83]" data-id="element-830" />
                </div>
                <div className="font-serif text-4xl font-bold text-[#2C3E50] mb-2" data-id="element-831">
                  {achievement.number}
                </div>
                <div className="text-[#5D6D7E]" data-id="element-832">{achievement.label}</div>
              </motion.div>)}
          </div>
        </div>
      </section>

      {/* Biography */}
      <section className="py-20 bg-[#FAF7F2]" data-id="element-833">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-834">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-835">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-836">
              The Journey of a Master
            </h2>

            <div className="prose prose-lg max-w-none mb-16" data-id="element-837">
              <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-838">
                Satguru Dev's life is a testament to the transformative power of
                dedicated spiritual practice. Born in 1950 in a remote Himalayan
                village, he was drawn to meditation and contemplation from an
                early age. His childhood was marked by profound questions about
                the nature of existence, consciousness, and the purpose of human
                life.
              </p>

              <p className="text-lg text-[#5D6D7E] leading-relaxed mb-6" data-id="element-839">
                At fifteen, against his family's wishes, he embarked on a
                spiritual pilgrimage that would define his destiny. Walking
                barefoot through the mountains, he sought out enlightened
                masters, spending years in caves and ashrams, absorbing wisdom
                from multiple traditions—Vedanta, Buddhism, Tantra, and Sufism.
              </p>

              <p className="text-lg text-[#5D6D7E] leading-relaxed" data-id="element-840">
                His teaching is characterized by clarity, compassion, and a rare
                ability to make ancient wisdom relevant to modern life. He
                emphasizes direct experience over dogma, encouraging students to
                verify spiritual truths through their own practice rather than
                blind belief.
              </p>
            </div>

            {/* Timeline */}
            <div className="space-y-8" data-id="element-841">
              {timeline.map((period, index) => <motion.div key={index} initial={{
              opacity: 0,
              x: -20
            }} whileInView={{
              opacity: 1,
              x: 0
            }} viewport={{
              once: true
            }} transition={{
              duration: 0.5,
              delay: index * 0.1
            }} className="bg-white p-8 rounded-2xl shadow-md border-l-4 border-[#D4AF37]" data-id="element-842">
                  <div className="text-sm text-[#D4AF37] font-semibold mb-2" data-id="element-843">
                    {period.period}
                  </div>
                  <h3 className="font-serif text-2xl text-[#2C3E50] mb-3" data-id="element-844">
                    {period.title}
                  </h3>
                  <p className="text-[#5D6D7E] leading-relaxed" data-id="element-845">
                    {period.description}
                  </p>
                </motion.div>)}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Teaching Philosophy */}
      <section className="py-20 bg-white" data-id="element-846">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-847">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-848">
            <h2 className="font-serif text-4xl text-[#2C3E50] mb-12 text-center" data-id="element-849">
              Teaching Philosophy
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-850">
              <div className="bg-[#FAF7F2] p-8 rounded-2xl" data-id="element-851">
                <h3 className="font-serif text-2xl text-[#8B9D83] mb-4" data-id="element-852">
                  Direct Experience
                </h3>
                <p className="text-[#5D6D7E] leading-relaxed" data-id="element-853">
                  "Truth cannot be taught, only experienced. My role is not to
                  give you answers, but to guide you to discover your own inner
                  wisdom through practice and inquiry."
                </p>
              </div>

              <div className="bg-[#FAF7F2] p-8 rounded-2xl" data-id="element-854">
                <h3 className="font-serif text-2xl text-[#8B9D83] mb-4" data-id="element-855">
                  Integration
                </h3>
                <p className="text-[#5D6D7E] leading-relaxed" data-id="element-856">
                  "Spirituality is not separate from daily life. The meditation
                  cushion is training ground; the real practice happens in every
                  moment of living with awareness."
                </p>
              </div>

              <div className="bg-[#FAF7F2] p-8 rounded-2xl" data-id="element-857">
                <h3 className="font-serif text-2xl text-[#8B9D83] mb-4" data-id="element-858">
                  Compassion
                </h3>
                <p className="text-[#5D6D7E] leading-relaxed" data-id="element-859">
                  "True awakening naturally gives rise to compassion. As you
                  discover your own inner light, you recognize that same light
                  in all beings."
                </p>
              </div>

              <div className="bg-[#FAF7F2] p-8 rounded-2xl" data-id="element-860">
                <h3 className="font-serif text-2xl text-[#8B9D83] mb-4" data-id="element-861">
                  Simplicity
                </h3>
                <p className="text-[#5D6D7E] leading-relaxed" data-id="element-862">
                  "The path is simple, though not always easy. Return to the
                  breath, return to the present moment, return to your true
                  nature—again and again."
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-br from-[#8B9D83] to-[#7A8C72] text-white" data-id="element-863">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-864">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-865">
            <h2 className="font-serif text-4xl md:text-5xl mb-6" data-id="element-866">
              Learn from Satguru Dev
            </h2>
            <p className="text-xl mb-10 text-white/90" data-id="element-867">
              Experience his teachings firsthand through our retreats and
              courses
            </p>
            <button className="bg-white text-[#8B9D83] hover:bg-[#FAF7F2] px-12 py-4 rounded-full font-semibold text-lg transition-all duration-300 transform hover:scale-105 shadow-xl" data-id="element-868">
              View Upcoming Courses
            </button>
          </motion.div>
        </div>
      </section>

      <Footer data-id="element-869" />
    </div>;
}